import { captureErrorFx } from 'model/helpers/analytics.model';
import { Component, h } from 'preact';
import enumerateDevices from 'enumerate-devices';

export const backCameraKeywords = [
	'rear',
	'back',
	'world',
	'rück',
	'arrière',
	'trasera',
	'trás',
	'traseira',
	'posteriore',
	'后面',
	'後面',
	'背面',
	'后置', // alternative
	'後置', // alternative
	'背置', // alternative
	'задней',
	'الخلفية',
	'후',
	'arka',
	'achterzijde',
	'หลัง',
	'baksidan',
	'bagside',
	'sau',
	'bak',
	'tylny',
	'takakamera',
	'אחורית',
	'πίσω',
	'spate',
	'hátsó',
	'zadní',
	'darrere',
	'zadná',
	'задня',
	'stražnja',
	'belakang',
	'बैक',
];

const mediaDevices = navigator.mediaDevices;
const getUserMedia = mediaDevices && mediaDevices.getUserMedia ? mediaDevices.getUserMedia.bind(mediaDevices) : null;
const hasGetUserMedia = !!(getUserMedia);

const handleFacingModeConstraints = (constraints) => {
	if (constraints && typeof constraints.video === 'object') {
		const { facingMode } = constraints.video;
		const shouldUseBackCam = facingMode === 'environment' || (typeof facingMode === 'object' && facingMode.exact && facingMode.exact === 'environment');
		if (!shouldUseBackCam) {
			return constraints;
		}
		return enumerateDevices().then(devices => {
			const cameras = extractCamerasFromDevices(devices);

			const mainBackCam = mainBackCamera(cameras);
			if (mainBackCam && mainBackCam.deviceId === '') {
				constraints.video.facingMode = { ideal: 'environment' };
			} else {
				const deviceId = mainBackCam && mainBackCam.deviceId ? mainBackCam.deviceId : cameras[0].deviceId;
				constraints.video.deviceId = { exact: deviceId };
			}
			return constraints;
		});
	}
};

const isBackCameraLabel = (label) => {
	const lowercaseLabel = label.toLowerCase();

	return backCameraKeywords.some(keyword => {
		return lowercaseLabel.includes(keyword);
	});
};

const cameraObjects = new Map();

const extractCamerasFromDevices = (devices) => {
	const cameras = devices
		.filter(device => {
			return device.kind === 'videoinput';
		})
		.map(videoDevice => {
			if (cameraObjects.has(videoDevice.deviceId)) {
				return cameraObjects.get(videoDevice.deviceId);
			}

			const label = videoDevice.label !== null ? videoDevice.label : '';
			const camera = {
				deviceId: videoDevice.deviceId,
				label,
				cameraType: isBackCameraLabel(label) ? 'back' : 'front',
			};

			if (label !== '') {
				cameraObjects.set(videoDevice.deviceId, camera);
			}

			return camera;
		});
	if (
		cameras.length > 1 &&
		!cameras.some(camera => {
			return camera.cameraType === 'back';
		})
	) {
		let backCameraIndex = cameras.length - 1;

		const cameraResolutions = cameras.map(camera => {
			const match = camera.label.match(/\b([0-9]+)MP?\b/i);
			if (match) {
				return parseInt(match[1], 10);
			}

			return NaN;
		});
		if (
			!cameraResolutions.some(cameraResolution => {
				return isNaN(cameraResolution);
			})
		) {
			backCameraIndex = cameraResolutions.lastIndexOf(Math.max(...cameraResolutions));
		}
		(cameras[backCameraIndex]).cameraType = 'back';
	}

	return cameras;
};

const mainBackCamera = (cameras) => cameras.filter(camera => camera.cameraType === 'back')
	.sort((camera1, camera2) => camera1.label.localeCompare(camera2.label))[0];

type constraintTypes = number | Object;
type facingModeLiterals = 'user' | 'environment';
type facingModeType = facingModeLiterals | { exact: facingModeLiterals };

type CameraType = {
	audio?: boolean,
	onUserMedia: Function,
	onFailure: Function,
	height?: constraintTypes,
	width?: constraintTypes,
	fallbackHeight?: constraintTypes,
	fallbackWidth?: constraintTypes,
	facingMode?: facingModeType,
	screenshotFormat?: 'image/webp' |
		'image/png' |
		'image/jpeg',
	className?: String,
}

const permissionErrors = ['PermissionDeniedError', 'NotAllowedError', 'NotFoundError'];

const stopStreamTracks = (stream: MediaStream) => {
	if (stream && stream.getVideoTracks) {
		for (let track of stream.getVideoTracks()) {
			track.stop();
		}
	}
	if (stream && stream.getAudioTracks) {
		for (let track of stream.getAudioTracks()) {
			track.stop();
		}
	}
};

export default class Webcam extends Component {
	props: any;

	static mountedInstances = [];

	static userMediaRequested = false;

	state = {
		hasUserMedia: false,
		mirrored: false,
	};

	stream: MediaStream;
	canvas: HTMLCanvasElement;
	ctx: CanvasRenderingContext2D;
	video?: HTMLVideoElement;
	recordedBlobs: Array<any>;
	mediaRecorder: Object;

	constructor(props: CameraType) {
		super(props);

		if (!hasGetUserMedia) {
			const error = new Error('getUserMedia is not supported by this browser');
			this.props.onFailure(error);
		}
	}

	async componentDidMount() {
		Webcam.mountedInstances.push(this);
		await this.requestUserMedia();
	}

	getConstraints(width: any, height: any, facingMode?: facingModeType, audio?: boolean): Object {
		const constraints: any = { video: { facingMode }, audio };

		if (width) {
			constraints.video.width = parseInt(width, 10) || width; // some devices need a Number type
		}

		if (height) {
			constraints.video.height = parseInt(height, 10) || height;
		}

		return constraints;
	}

	async requestUserMedia() {
		if (!getUserMedia || !mediaDevices || Webcam.userMediaRequested) {
			return;
		}
		const { width, height, facingMode, audio, fallbackWidth, fallbackHeight } = this.props;

		const constraints = this.getConstraints(width, height, facingMode, audio);
		const fallbackConstraints = this.getConstraints(fallbackWidth, fallbackHeight, facingMode, audio);

		const logError = e => console.log('error', e, typeof e);

		const onSuccess = stream => {
			Webcam.userMediaRequested = false;
			Webcam.mountedInstances.forEach((instance) => instance.handleUserMedia(stream));
		};

		let hasTriedFallbackConstraints;
		const onError = e => {
			Webcam.userMediaRequested = false;
			logError(e);
			const isPermissionError = permissionErrors.includes(e.name);
			if (isPermissionError || hasTriedFallbackConstraints) {
				Webcam.mountedInstances.forEach((instance) => instance.handleError(e));
			} else {
				hasTriedFallbackConstraints = true;
				getUserMedia(fallbackConstraints).then(onSuccess).catch(onError);
			}
		};
		Webcam.userMediaRequested = true;
		try {
			this.stream = await getUserMedia(await handleFacingModeConstraints(constraints));
			if (this.stream) {
				onSuccess(this.stream);
			}
		} catch (e) {
			captureErrorFx(e);
			onError(e);
		}

	}

	handleError(error: Object) {
		// @ts-ignore
		this.setState({
			hasUserMedia: false,
		});
		this.props.onFailure(error);
	}

	handleUserMedia(stream: MediaStream) {
		const videoSettings = stream ? stream.getVideoTracks()[0].getSettings() : {};
		this.stream = stream;
		const facingMode = this.props.facingMode;
		const isVideoStreamForRearCamera = (facingMode === 'environment' || (facingMode && facingMode.exact && facingMode.exact === 'environment'));

		// @ts-ignore
		this.setState({
			hasUserMedia: true,
			mirrored: !isVideoStreamForRearCamera
				&& (videoSettings.facingMode === 'user'
					|| !videoSettings.facingMode),
		});

		this.props.onUserMedia();
	}

	componentWillUnmount() {
		const index = Webcam.mountedInstances.indexOf(this);
		Webcam.mountedInstances.splice(index, 1);
		const stopDelay = 1000;
		setTimeout(() => stopStreamTracks(this.stream), stopDelay);
	}

	getScreenshot() {
		const canvas = this.getCanvas();
		if (!canvas) {
			return null;
		}
		return canvas.toDataURL(this.props.screenshotFormat);
	}

	getCanvas(): HTMLCanvasElement | null {
		if (!this.state.hasUserMedia || !this.video) {
			return null;
		}

		const video = this.video;

		if (!this.canvas) {
			this.canvas = document.createElement('canvas');
		}
		const { canvas } = this;

		if (!this.ctx) {
			this.ctx = canvas.getContext('2d');
		}
		const { ctx } = this;

		canvas.width = video.videoWidth;
		canvas.height = video.videoHeight;

		ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
		return canvas;
	}

	render() {
		if (!this.stream) {
			return null;
		}

		return (
			<video
				style={{
					transform: this.state.mirrored ? 'scaleX(-1)' : '',
				}}
				ref={(el) => this.video = el}
				autoPlay
				// @ts-ignore
				playsinline
				srcObject={this.stream}
				muted={true}
				className={this.props.className}
			/>
		);
	}
}
